import React, { useEffect, useRef, useState } from 'react';
import { MONTHS } from '../DateFields/DateFields';
import { searchPlaces } from '../../services/chartService';
import './BirthFields.css';

function fill(template, values = {}) {
  return String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
    values[key] == null ? match : String(values[key])
  ));
}

const DEFAULT_MESSAGES = {
  dateError: 'enter a real birth date.',
  yearError: 'choose a year from {min} through {max}.',
  timeError: 'enter a real birth time.',
  timeRequired: 'enter a birth time, or tick "{unknown}".',
  placeError: 'choose a birthplace from the list.',
  unknown: "I don't know the time",
};

export const emptyBirth = (name = '') => ({
  name,
  month: '',
  day: '',
  year: '',
  hour: '',
  minute: '',
  ampm: 'AM',
  timeUnknown: false,
  placeQuery: '',
  place: null,
});

function toHour24(hour, ampm) {
  const value = Number(hour);
  if (ampm === 'AM') return value === 12 ? 0 : value;
  return value === 12 ? 12 : value + 12;
}

// Returns { error } or the fields the chart API expects.
export function birthPayload(value, { requirePlace = true, requireTime = false, who = '', messages } = {}) {
  const msg = { ...DEFAULT_MESSAGES, ...messages };
  const month = Number(value.month);
  const day = Number(value.day);
  const year = Number(value.year);
  const prefix = who ? `${who}: ` : '';
  const real = new Date(year, month - 1, day);
  if (!month || !day || !year || real.getFullYear() !== year || real.getMonth() !== month - 1 || real.getDate() !== day) {
    return { error: `${prefix}${msg.dateError}` };
  }
  if (year < 1900 || year > new Date().getFullYear()) {
    return { error: `${prefix}${fill(msg.yearError, { min: 1900, max: new Date().getFullYear() })}` };
  }
  const payload = { name: (value.name || '').trim(), year, month, day, hour: null, minute: null, time_unknown: true };
  const hasTime = value.hour !== '' && !value.timeUnknown;
  if (hasTime) {
    const hour = Number(value.hour);
    const minute = value.minute === '' ? 0 : Number(value.minute);
    if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return { error: `${prefix}${msg.timeError}` };
    payload.hour = toHour24(hour, value.ampm);
    payload.minute = minute;
    payload.time_unknown = false;
  } else if (requireTime && !value.timeUnknown) {
    return { error: `${prefix}${fill(msg.timeRequired, { unknown: msg.unknown })}` };
  }
  if (value.place) {
    payload.latitude = value.place.latitude;
    payload.longitude = value.place.longitude;
    payload.timezone = value.place.timezone;
    payload.place_label = value.place.label;
  } else if (requirePlace || (value.placeQuery || '').trim()) {
    return { error: `${prefix}${msg.placeError}` };
  }
  return payload;
}

function PlaceField({ value, onChange, label, optional, labels }) {
  const [places, setPlaces] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef(null);
  const query = value.placeQuery;
  const chosen = value.place?.label === query;

  useEffect(() => {
    if (chosen || query.trim().length < 2) {
      setPlaces([]);
      return undefined;
    }
    let live = true;
    setSearching(true);
    const timer = setTimeout(() => {
      searchPlaces(query.trim())
        .then((found) => { if (live) { setPlaces(found); setOpen(true); } })
        .catch(() => { if (live) setPlaces([]); })
        .finally(() => { if (live) setSearching(false); });
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [query, chosen]);

  useEffect(() => {
    const close = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="birth-fields-place" ref={boxRef}>
      <label>
        {label}{optional ? <span className="birth-fields-optional"> {labels.optional}</span> : null}
        <input
          value={query}
          autoComplete="off"
          placeholder={labels.placePlaceholder}
          onFocus={() => places.length && setOpen(true)}
          onChange={(event) => onChange({ ...value, placeQuery: event.target.value, place: null })}
        />
      </label>
      {searching && <span className="birth-fields-hint">{labels.searching}</span>}
      {open && places.length > 0 && !chosen && (
        <ul className="birth-fields-places" role="listbox">
          {places.map((place) => (
            <li key={`${place.label}-${place.latitude}`}>
              <button
                type="button"
                onClick={() => {
                  onChange({ ...value, placeQuery: place.label, place });
                  setOpen(false);
                }}
              >
                {place.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const DEFAULT_LABELS = {
  optional: '(optional)',
  date: 'Date of birth',
  month: 'Month',
  day: 'Day',
  year: 'Year',
  time: 'Time of birth',
  hour: 'Hour',
  minute: 'Min',
  minuteLabel: 'Minute',
  unknown: "I don't know the time",
  place: 'Place of birth',
  placePlaceholder: 'Start typing a city',
  searching: 'Searching…',
  months: MONTHS,
};

const BirthFields = ({ value, onChange, showName = false, nameLabel = 'Name', placeOptional = false, timeOptional = false, labels: labelOverrides }) => {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="birth-fields">
      {showName && (
        <label className="birth-fields-name">
          {nameLabel}<span className="birth-fields-optional"> {labels.optional}</span>
          <input value={value.name} maxLength={80} onChange={(event) => set({ name: event.target.value })} />
        </label>
      )}
      <fieldset className="birth-fields-group">
        <legend>{labels.date}</legend>
        <div className="birth-fields-row">
          <select aria-label={labels.month} value={value.month} onChange={(event) => set({ month: event.target.value })}>
            <option value="">{labels.month}</option>
            {labels.months.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
          </select>
          <input
            aria-label={labels.day}
            inputMode="numeric"
            placeholder={labels.day}
            value={value.day}
            onChange={(event) => set({ day: event.target.value.replace(/\D/g, '').slice(0, 2) })}
          />
          <input
            aria-label={labels.year}
            inputMode="numeric"
            placeholder={labels.year}
            value={value.year}
            onChange={(event) => set({ year: event.target.value.replace(/\D/g, '').slice(0, 4) })}
          />
        </div>
      </fieldset>
      <fieldset className="birth-fields-group">
        <legend>{labels.time}{timeOptional ? <span className="birth-fields-optional"> {labels.optional}</span> : null}</legend>
        <div className="birth-fields-row">
          <input
            aria-label={labels.hour}
            inputMode="numeric"
            placeholder={labels.hour}
            disabled={value.timeUnknown}
            value={value.hour}
            onChange={(event) => set({ hour: event.target.value.replace(/\D/g, '').slice(0, 2) })}
          />
          <input
            aria-label={labels.minuteLabel}
            inputMode="numeric"
            placeholder={labels.minute}
            disabled={value.timeUnknown}
            value={value.minute}
            onChange={(event) => set({ minute: event.target.value.replace(/\D/g, '').slice(0, 2) })}
          />
          <select aria-label="AM or PM" disabled={value.timeUnknown} value={value.ampm} onChange={(event) => set({ ampm: event.target.value })}>
            <option>AM</option>
            <option>PM</option>
          </select>
        </div>
        {!timeOptional && (
          <label className="birth-fields-check">
            <input type="checkbox" checked={value.timeUnknown} onChange={(event) => set({ timeUnknown: event.target.checked })} />
            {labels.unknown}
          </label>
        )}
        {!timeOptional && labels.timeNote ? <p className="birth-time-note">{labels.timeNote}</p> : null}
      </fieldset>
      <PlaceField value={value} onChange={onChange} label={labels.place} optional={placeOptional} labels={labels} />
    </div>
  );
};

export default BirthFields;
